import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Trash2, Mail, Clock, Sparkles, Loader2, ExternalLink, Inbox, FileText, ShieldCheck, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { regionLabel } from "@/lib/regions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const CATEGORY_LABEL = { coop: "Co-op", tutor: "Tutor" };

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

function StatusPill({ status }) {
  const map = {
    pending: "bg-amber-50 text-amber-800 border-amber-100",
    approved: "bg-emerald-50 text-emerald-800 border-emerald-100",
    rejected: "bg-red-50 text-red-800 border-red-100",
  };
  return (
    <span className={`rounded-full border text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 ${map[status] || ""}`}>
      {status}
    </span>
  );
}

export default function AdminSuggestions() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState(null);

  const load = async () => {
    setItems(null);
    try {
      const r = await axios.get(`${API}/resource-suggestions/admin`, { params: { status: tab } });
      setItems(r.data);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Admin access required");
      else toast.error("Failed to load suggestions");
      setItems([]);
    }
  };

  useEffect(() => { load(); }, [tab]);

  const patch = async (id, changes, successMsg) => {
    try {
      await axios.patch(`${API}/resource-suggestions/admin/${id}`, changes);
      if (successMsg) toast.success(successMsg);
      load();
    } catch { toast.error("Update failed"); }
  };

  const onDelete = async (sug) => {
    setItems((prev) => prev.filter((x) => x.id !== sug.id));
    let undone = false;
    toast(`"${sug.name}" removed`, {
      duration: 10000,
      action: { label: "Undo", onClick: () => { undone = true; load(); } },
    });
    setTimeout(async () => {
      if (undone) return;
      try { await axios.delete(`${API}/resource-suggestions/admin/${sug.id}`); }
      catch { toast.error("Delete failed"); load(); }
    }, 10000);
  };

  const pendingCount = tab === "pending" && items ? items.length : null;

  return (
    <div className="space-y-8" data-testid="admin-suggestions-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-amber-600 font-semibold">Admin</div>
        <h1 className="mt-1 font-display text-3xl font-bold flex items-center gap-3">
          Resource suggestions
          {pendingCount > 0 && (
            <span className="text-sm font-medium bg-amber-100 text-amber-800 rounded-full px-2.5 py-0.5" data-testid="pending-count">
              {pendingCount} pending
            </span>
          )}
        </h1>
        <p className="mt-1 text-stone-600 text-sm">User-submitted Co-ops and Tutors awaiting review.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full bg-white border border-stone-200 p-1.5">
          <TabsTrigger value="pending" className="rounded-full data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="sug-tab-pending">
            <Clock className="w-3.5 h-3.5 mr-1.5" /> Pending
          </TabsTrigger>
          <TabsTrigger value="approved" className="rounded-full data-[state=active]:bg-emerald-500 data-[state=active]:text-white" data-testid="sug-tab-approved">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Approved
          </TabsTrigger>
          <TabsTrigger value="rejected" className="rounded-full data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="sug-tab-rejected">
            <XCircle className="w-3.5 h-3.5 mr-1.5" /> Rejected
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-6">
          {items === null ? (
            <div className="text-stone-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-10 text-center text-stone-500" data-testid="empty-suggestions">
              <Inbox className="w-8 h-8 mx-auto mb-2 text-stone-400" />
              No {tab} suggestions.
            </div>
          ) : (
            <div className="grid gap-4" data-testid="suggestions-list">
              {items.map((s) => (
                <div key={s.id} className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={`sug-row-${s.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="rounded-full bg-stone-100 text-stone-700 text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5">
                          {CATEGORY_LABEL[s.category] || s.category}
                        </span>
                        <span className="rounded-full bg-amber-50 text-amber-800 border border-amber-100 text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5">
                          {regionLabel(s.region)}
                        </span>
                        <StatusPill status={s.status} />
                        <span className="text-xs text-stone-500 ml-auto">{fmt(s.created_at)}</span>
                      </div>
                      <h3 className="mt-2 font-display font-semibold text-lg leading-snug flex items-center gap-2" data-testid={`sug-name-${s.id}`}>
                        {s.name}
                        {s.verified && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]">
                            <BadgeCheck className="w-3 h-3" /> Verified
                          </span>
                        )}
                      </h3>
                      <p className="mt-2 text-stone-700 whitespace-pre-wrap text-sm leading-relaxed">{s.description}</p>

                      {(s.teaching_philosophy || s.florida_expertise) && (
                        <div className="mt-3 rounded-xl bg-amber-50/60 border border-amber-100 p-3 space-y-2 text-sm">
                          {s.teaching_philosophy && (
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-amber-700">Teaching philosophy</div>
                              <p className="mt-0.5 text-stone-700 whitespace-pre-wrap">{s.teaching_philosophy}</p>
                            </div>
                          )}
                          {s.florida_expertise && (
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-amber-700">Florida expertise</div>
                              <p className="mt-0.5 text-stone-700 whitespace-pre-wrap">{s.florida_expertise}</p>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mt-3 flex items-center gap-4 flex-wrap text-xs text-stone-500">
                        {s.url && (
                          <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 font-medium">
                            <ExternalLink className="w-3 h-3" /> {s.url}
                          </a>
                        )}
                        {s.contact && <span>Contact: <strong className="text-stone-700">{s.contact}</strong></span>}
                        {s.submitter_email && (
                          <a
                            href={`mailto:${s.submitter_email}?subject=Re: your HomeSchool Hub resource suggestion`}
                            className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 font-medium"
                            data-testid={`sug-reply-${s.id}`}
                          >
                            <Mail className="w-3 h-3" /> {s.submitter_name || "Submitter"} · {s.submitter_email}
                          </a>
                        )}
                        {s.has_resume && (
                          <a
                            href={`${API}/resource-suggestions/admin/${s.id}/resume`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-violet-700 hover:text-violet-900 font-medium"
                            data-testid={`sug-resume-${s.id}`}
                          >
                            <FileText className="w-3 h-3" /> Resume ({s.resume_filename || "attached"})
                            <ShieldCheck className="w-3 h-3 text-emerald-500 ml-0.5" title="Admin-only" />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-none">
                      <div className="flex items-center gap-2 rounded-full bg-stone-50 border border-stone-200 px-3 py-1.5">
                        <Label htmlFor={`vfy-${s.id}`} className="text-xs text-stone-700 cursor-pointer flex items-center gap-1">
                          <BadgeCheck className={`w-3.5 h-3.5 ${s.verified ? "text-emerald-500" : "text-stone-400"}`} /> Verified
                        </Label>
                        <Switch id={`vfy-${s.id}`} checked={!!s.verified} onCheckedChange={(v) => patch(s.id, { verified: v }, v ? "Marked verified" : "Verification removed")} data-testid={`toggle-verified-${s.id}`} />
                      </div>
                      {s.status !== "approved" && (
                        <Button size="sm" onClick={() => patch(s.id, { status: "approved" }, "Approved")} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs" data-testid={`approve-${s.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                      )}
                      {s.status !== "rejected" && (
                        <Button size="sm" variant="outline" onClick={() => patch(s.id, { status: "rejected" }, "Rejected")} className="rounded-full text-xs" data-testid={`reject-${s.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      )}
                      {s.status !== "pending" && (
                        <Button size="sm" variant="ghost" onClick={() => patch(s.id, { status: "pending" }, "Moved to pending")} className="rounded-full text-xs text-stone-500" data-testid={`repending-${s.id}`}>
                          <Clock className="w-3.5 h-3.5 mr-1" /> Re-queue
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => onDelete(s)} className="rounded-full text-xs text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-${s.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
